export const START_NODE_ID = '__start__';

export const NODE_TYPES = [
  'message',
  'whatsapp_list',
  'buttons',
  'handoff',
  'end',
];

export const NODE_META = {
  start: { icon: 'i-lucide-play', color: 'bg-n-slate-9' },
  message: { icon: 'i-lucide-message-square', color: 'bg-n-iris-9' },
  whatsapp_list: { icon: 'i-lucide-list', color: 'bg-n-teal-9' },
  buttons: { icon: 'i-lucide-square-menu', color: 'bg-n-amber-9' },
  media: { icon: 'i-lucide-image', color: 'bg-n-iris-9' },
  handoff: { icon: 'i-lucide-headset', color: 'bg-n-ruby-9' },
  end: { icon: 'i-lucide-flag', color: 'bg-n-slate-9' },
};

let counter = 0;
export const generateNodeId = type => {
  counter += 1;
  return `node_${type}_${Date.now().toString(36)}${counter}`;
};

export const slugify = text =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24) || `opt_${Date.now().toString(36)}`;

export const defaultDataFor = (type, t) => {
  switch (type) {
    case 'message':
      return {
        title: t('FLOWS.BUILDER.NODES.MESSAGE'),
        config: { text: '' },
      };
    case 'whatsapp_list':
      return {
        title: t('FLOWS.BUILDER.NODES.WHATSAPP_LIST'),
        config: {
          body: '',
          footer: '',
          button_text: t('FLOWS.BUILDER.DEFAULTS.BUTTON_TEXT'),
          sections: [
            {
              title: t('FLOWS.BUILDER.DEFAULTS.SECTION_TITLE'),
              rows: [
                {
                  id: `opcao_1`,
                  title: t('FLOWS.BUILDER.DEFAULTS.OPTION_TITLE'),
                  description: '',
                },
              ],
            },
          ],
        },
      };
    case 'buttons':
      return {
        title: t('FLOWS.BUILDER.NODES.BUTTONS'),
        config: {
          body: '',
          buttons: [
            { id: 'botao_1', title: t('FLOWS.BUILDER.DEFAULTS.OPTION_TITLE') },
          ],
        },
      };
    case 'media':
      return {
        title: t('FLOWS.BUILDER.NODES.MEDIA'),
        config: { media_type: 'image', media_asset_id: null, caption: '', filename: '' },
      };
    case 'handoff':
      return {
        title: t('FLOWS.BUILDER.NODES.HANDOFF'),
        config: { message: '', labels: [] },
      };
    case 'end':
      return {
        title: t('FLOWS.BUILDER.NODES.END'),
        config: { text: '', resolve_conversation: false },
      };
    default:
      return { title: type, config: {} };
  }
};

export const createCanvasNode = (type, position, t) => ({
  id: generateNodeId(type),
  type,
  position,
  data: defaultDataFor(type, t),
});

// preconfigured "fallback" block: just a message node with helpful defaults
export const createFallbackNode = (position, t) => {
  const node = createCanvasNode('message', position, t);
  node.data.title = t('FLOWS.BUILDER.NODES.FALLBACK');
  node.data.config.text = t('FLOWS.BUILDER.DEFAULTS.FALLBACK_TEXT');
  return node;
};

// ---------------------------------------------------------------------------
// mandated example flow, in the exact engine schema format (positions included)
// ---------------------------------------------------------------------------
export const EXAMPLE_SCHEMA = {
  start_node_id: 'menu_inicial',
  ui: { start_position: { x: -300, y: 60 } },
  nodes: [
    {
      id: 'menu_inicial',
      type: 'whatsapp_list',
      title: 'Menu inicial',
      position: { x: 0, y: 0 },
      config: {
        body: 'Olá! Seja bem-vindo à Segue Mais Você 🚀\nComo posso te ajudar hoje?',
        footer: 'Segue Mais Você 🚀',
        button_text: 'Escolher opção',
        sections: [
          {
            title: 'Atendimento',
            rows: [
              { id: 'nova_compra', title: 'Nova compra', description: 'Quero conhecer os pacotes', target_node_id: 'escolher_servico' },
              { id: 'problema_pedido', title: 'Problema com pedido', description: 'Preciso de ajuda com uma entrega', target_node_id: 'tempo_pedido' },
              { id: 'reposicao', title: 'Reposição', description: 'Quero solicitar reposição', target_node_id: 'prazo_reposicao' },
              { id: 'humano', title: 'Falar com atendente', description: 'Quero atendimento humano', target_node_id: 'transferir_humano' },
            ],
          },
        ],
        fallback_target_node_id: 'msg_fallback',
      },
    },
    {
      id: 'escolher_servico',
      type: 'whatsapp_list',
      title: 'Escolher serviço',
      position: { x: 420, y: -260 },
      config: {
        body: 'Certo! Me informe qual tipo de serviço você deseja hoje:',
        button_text: 'Escolher serviço',
        sections: [
          {
            title: 'Serviços',
            rows: [
              { id: 'seguidores', title: 'Seguidores', description: '', target_node_id: 'msg_seguidores' },
              { id: 'curtidas', title: 'Curtidas', description: '', target_node_id: 'msg_curtidas' },
              { id: 'views', title: 'Views', description: '', target_node_id: 'msg_views' },
              { id: 'combo', title: 'Combo completo', description: '', target_node_id: 'msg_combo' },
              { id: 'srv_atendente', title: 'Falar com atendente', description: '', target_node_id: 'transferir_humano' },
            ],
          },
        ],
        fallback_target_node_id: 'msg_fallback',
      },
    },
    {
      id: 'tempo_pedido',
      type: 'buttons',
      title: 'Tempo do pedido',
      position: { x: 420, y: 120 },
      config: {
        body: 'Para eu te ajudar melhor, há quanto tempo você fez seu pedido?',
        buttons: [
          { id: 'menos_2h', title: 'Menos de 2 horas', target_node_id: 'msg_menos_2h' },
          { id: 'cerca_12h', title: 'Cerca de 12 horas', target_node_id: 'msg_cerca_12h' },
          { id: 'mais_24h', title: 'Mais de 24 horas', target_node_id: 'msg_mais_24h' },
        ],
        fallback_target_node_id: 'msg_fallback',
      },
    },
    {
      id: 'prazo_reposicao',
      type: 'buttons',
      title: 'Prazo de reposição',
      position: { x: 420, y: 420 },
      config: {
        body: 'Sua última compra foi feita há quanto tempo?',
        buttons: [
          { id: 'menos_30d', title: 'Há menos de 30 dias', target_node_id: 'msg_menos_30d' },
          { id: 'mais_30d', title: 'Há mais de 30 dias', target_node_id: 'msg_mais_30d' },
        ],
        fallback_target_node_id: 'msg_fallback',
      },
    },
    { id: 'msg_seguidores', type: 'message', title: 'Seguidores', position: { x: 860, y: -400 }, config: { text: 'Ótima escolha! Vou te passar para um atendente fechar seu pacote de seguidores. 😉' }, next_node_id: 'transferir_humano' },
    { id: 'msg_curtidas', type: 'message', title: 'Curtidas', position: { x: 860, y: -280 }, config: { text: 'Ótima escolha! Vou te passar para um atendente fechar seu pacote de curtidas. 😉' }, next_node_id: 'transferir_humano' },
    { id: 'msg_views', type: 'message', title: 'Views', position: { x: 860, y: -160 }, config: { text: 'Ótima escolha! Vou te passar para um atendente fechar seu pacote de views. 😉' }, next_node_id: 'transferir_humano' },
    { id: 'msg_combo', type: 'message', title: 'Combo completo', position: { x: 860, y: -40 }, config: { text: 'O combo completo é nosso melhor custo-benefício! Um atendente vai te apresentar as opções. 🚀' }, next_node_id: 'transferir_humano' },
    { id: 'msg_menos_2h', type: 'message', title: 'Pedido < 2h', position: { x: 860, y: 80 }, config: { text: 'Seu pedido ainda está em processamento. Normalmente conclui em até 2 horas. Aguarde só mais um pouquinho! ⏳' }, next_node_id: 'fim' },
    { id: 'msg_cerca_12h', type: 'message', title: 'Pedido ~ 12h', position: { x: 860, y: 200 }, config: { text: 'Entendi. Vou pedir para um atendente verificar seu pedido agora mesmo.' }, next_node_id: 'transferir_humano' },
    { id: 'msg_mais_24h', type: 'message', title: 'Pedido > 24h', position: { x: 860, y: 320 }, config: { text: 'Sentimos muito pela demora! Vou te encaminhar com prioridade para um atendente resolver.' }, next_node_id: 'transferir_humano' },
    { id: 'msg_menos_30d', type: 'message', title: 'Compra < 30 dias', position: { x: 860, y: 440 }, config: { text: 'Sua compra está dentro da garantia de reposição! Um atendente vai processar sua reposição.' }, next_node_id: 'transferir_humano' },
    { id: 'msg_mais_30d', type: 'message', title: 'Compra > 30 dias', position: { x: 860, y: 560 }, config: { text: 'Infelizmente o prazo de reposição gratuita é de 30 dias. Mas fale com a gente para conhecer as condições especiais de recompra!' }, next_node_id: 'fim' },
    {
      id: 'transferir_humano',
      type: 'handoff',
      title: 'Transferir humano',
      position: { x: 1300, y: 60 },
      config: { message: 'Certo! Vou te encaminhar para um atendente agora.', labels: ['humano'] },
    },
    { id: 'fim', type: 'end', title: 'Finalizar', position: { x: 1300, y: 420 }, config: {} },
    { id: 'msg_fallback', type: 'message', title: 'Fallback', position: { x: 0, y: 420 }, config: { text: 'Não consegui identificar sua escolha. Por favor, selecione uma opção do menu.' }, next_node_id: 'menu_inicial' },
  ],
};
